import { useState, useCallback } from 'react';
import { p9301Api, EmployeeRow, QueryParams } from '../../../services/p9301Api';
import { message } from 'antd';

export interface P9301Filters {
  empFrom: string;
  empTo: string;
  deptFrom: string;
  deptTo: string;
}

const defaultFilters: P9301Filters = {
  empFrom: '',
  empTo: '',
  deptFrom: '',
  deptTo: '',
};

export function useP9301Query() {
  const [filters, setFilters] = useState<P9301Filters>({ ...defaultFilters });
  const [results, setResults] = useState<EmployeeRow[]>([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [queried, setQueried] = useState(false);

  /** Update a single filter field */
  const setFilter = useCallback((field: keyof P9301Filters, value: string) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  }, []);

  /** Reset all filters to defaults */
  const resetFilters = useCallback(() => {
    setFilters({ ...defaultFilters });
    setResults([]);
    setCount(0);
    setQueried(false);
  }, []);

  /**
   * BR-UI01: When empFrom loses focus, auto-fill empTo
   * e.g. "A001" -> "A001ZZZZZZZZ"
   */
  const handleEmpFromBlur = useCallback(() => {
    setFilters(prev => {
      if (prev.empFrom.trim()) {
        return { ...prev, empTo: prev.empFrom.trim() + 'ZZZZZZZZ' };
      }
      return prev;
    });
  }, []);

  /**
   * BR-UI03: When deptFrom loses focus, auto-fill deptTo
   * e.g. "A1" -> "A1ZZZZ"
   */
  const handleDeptFromBlur = useCallback(() => {
    setFilters(prev => {
      if (prev.deptFrom.trim()) {
        return { ...prev, deptTo: prev.deptFrom.trim() + 'ZZZZ' };
      }
      return prev;
    });
  }, []);

  /** Execute query with current filters */
  const executeQuery = useCallback(async () => {
    setLoading(true);
    try {
      const params: Partial<QueryParams> = {
        empFrom: filters.empFrom.trim() || undefined,
        empTo: filters.empTo.trim() || undefined,
        deptFrom: filters.deptFrom.trim() || undefined,
        deptTo: filters.deptTo.trim() || undefined,
      };
      const result = await p9301Api.query(params);
      setResults(result.data || []);
      setCount(result.count || 0);
      setQueried(true);

      if (result.message) {
        message.info(result.message);
      } else if ((result.data || []).length > 0) {
        message.success(`查詢完成，共 ${result.count} 筆資料`);
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { status?: number; data?: { details?: Array<{ message: string }> } } };
      if (axiosErr.response?.status === 422) {
        const details = axiosErr.response.data?.details || [];
        const msgs = details.map((d: { message: string }) => d.message);
        message.error(msgs.join('\n') || '驗證失敗');
      } else {
        message.error('查詢失敗');
      }
      setResults([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  return {
    filters,
    setFilter,
    resetFilters,
    handleEmpFromBlur,
    handleDeptFromBlur,
    results,
    count,
    loading,
    queried,
    executeQuery,
  };
}
